import React, { useEffect, useState } from 'react';

import LoadingAnimation from 'components/loading-animation';
import {
  IGeneratePaymarkCheckoutLinkMutation,
  useGeneratePaymarkCheckoutLinkMutation,
} from 'generated/rbi-graphql';
import { usePaymentContext } from 'state/payment';
import logger from 'utils/logger';

import { OutcomeResult } from '../types';

import { PaymarkHostedPaymentContainer } from './paymark-hosted-payment-form.styled';

export interface IPaymarkHostedPaymentFormProps {
  onResult: (outcome: OutcomeResult) => void;
  rbiOrderId: string;
}

export const PaymarkHostedPaymentForm: React.FC<IPaymarkHostedPaymentFormProps> = ({
  rbiOrderId,
  onResult,
}) => {
  const [paymentLink, setPaymentLink] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState('850px');
  const { paymentValues } = usePaymentContext();

  const handlePayMarkLinkCompleted = (result: IGeneratePaymarkCheckoutLinkMutation) => {
    setPaymentLink(result.generatePaymarkCheckoutLink.link);
  };

  const [generatePayMarkLink] = useGeneratePaymarkCheckoutLinkMutation({
    onCompleted: handlePayMarkLinkCompleted,
    onError: error => {
      logger.error({
        message: 'PayMark: Non-vaulted CC integration threw an error',
        error,
      });

      throw error;
    },
  });

  useEffect(() => {
    if (rbiOrderId) {
      generatePayMarkLink({
        variables: { rbiOrderId },
      });
    }
  }, [rbiOrderId, generatePayMarkLink, paymentValues.accountIdentifier]);

  useEffect(() => {
    if (!paymentLink) {
      return undefined;
    }

    function messageListenerFn(event: MessageEvent) {
      const origin = new URL(event.origin);
      if (!origin.hostname.endsWith('.rbictg.com')) {
        return;
      }
      if (event.data.type !== 'payment-outcome') {
        return;
      }

      const body = event.data.body as OutcomeResult;

      setIframeHeight('0px');

      onResult(body);
    }

    window.addEventListener('message', messageListenerFn);
    return () => window.removeEventListener('message', messageListenerFn);
  }, [paymentLink, onResult]);

  return (
    <PaymarkHostedPaymentContainer style={{ height: iframeHeight }}>
      {!paymentLink && !iframeLoaded && <LoadingAnimation fillColor={Styles.color.primary} />}
      {paymentLink && (
        <iframe
          onLoad={() => setIframeLoaded(true)}
          data-testid="hosted-payment-form"
          title="Payment Form"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            visibility: iframeLoaded ? 'visible' : 'hidden',
          }}
          src={paymentLink}
        ></iframe>
      )}
    </PaymarkHostedPaymentContainer>
  );
};
